import { ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@holigral/shared/ui/components/ui/card"
import { IconButton } from "@holigral/shared/ui/composed"
import {
  ApiFormWrapper,
  FormControl,
  FormSelect,
  FormSubmitButton,
  FormTextarea,
} from "@holigral/shared/form"
import BriefCombobox from "./BriefCombobox"
import parseEmails from "./parseEmails"
import { useSendAssignmentsMutation } from "./api"
import type { Page, TenantAssignment } from "./model"
import { useT } from "@/i18n"
import { useGetCurrentTenantQuery } from "@/tenant"
import {
  TopUp,
  sendAssignmentsAction,
  useCreditPrice,
  useGetBalanceQuery,
} from "@/money"
import type { Uuid } from "@/utils"

type SendFormValues = {
  emails: string | null
  locale: string
  personalMessage: string | null
  briefId: Uuid | null
}

export default function SendAssignmentsForm() {
  const { t } = useT("assignment")
  const [expanded, setExpanded] = useState(true)
  const [formKey, setFormKey] = useState(0)
  const [emailsRaw, setEmailsRaw] = useState("")
  const [sendAssignments] = useSendAssignmentsMutation()
  const { data: tenant } = useGetCurrentTenantQuery()
  const { data: balance } = useGetBalanceQuery()

  const defaultLocale = tenant?.defaultLocale ?? "en"
  // Mirror the form fields so the top-up can carry the queued send.
  // Locale is derived, not stored: hold only the user's explicit pick and fall
  // back to the live workspace default, so it tracks the tenant once it resolves
  // (a plain useState(defaultLocale) would freeze on the "en" loading fallback).
  const [chosenLocale, setChosenLocale] = useState<string | null>(null)
  const locale = chosenLocale ?? defaultLocale
  const [personalMessage, setPersonalMessage] = useState("")
  const [briefId, setBriefId] = useState<Uuid | null>(null)
  const [topUpOpen, setTopUpOpen] = useState(false)

  const { valid, invalid } = parseEmails(emailsRaw)
  const isOwner = tenant?.permissions.billingAdmin ?? false
  const shortfall = balance ? Math.max(0, valid.length - balance.available) : 0
  const unitPrice = useCreditPrice(1)
  const totalPrice = useCreditPrice(Math.max(1, valid.length))

  const localeOptions = [
    { label: t("shared:locales.en"), value: "en" },
    { label: t("shared:locales.es_MX"), value: "es_MX" },
  ]

  const action = (values: SendFormValues) => {
    const message = values.personalMessage?.trim() ?? ""

    return {
      unwrap: (): Promise<Page<TenantAssignment>> =>
        sendAssignments({
          emails: parseEmails(values.emails ?? "").valid,
          locale: values.locale,
          personalMessage: message || null,
          briefId: values.briefId,
        }).unwrap(),
    }
  }

  const resetForm = () => {
    setEmailsRaw("")
    setPersonalMessage("")
    setBriefId(null)
    // Remount the form so its fields clear too (they own their own state).
    setFormKey((key) => key + 1)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("send.title")}</CardTitle>
        <CardDescription>{t("send.description")}</CardDescription>
        <CardAction>
          <IconButton
            icon={expanded ? <ChevronUp /> : <ChevronDown />}
            aria-label={expanded ? t("send.collapse") : t("send.expand")}
            onClick={() => {
              setExpanded(!expanded)
            }}
          />
        </CardAction>
      </CardHeader>
      {expanded && (
        <CardContent>
          <ApiFormWrapper<SendFormValues>
            key={formKey}
            defaultValues={{
              emails: "",
              locale: defaultLocale,
              personalMessage: "",
              briefId: null,
            }}
            resetKey={defaultLocale}
            action={action}
            successMessage={t("send.success")}
            resetOnSuccess
            onSuccess={resetForm}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <FormTextarea
                  name="emails"
                  label={t("send.emails")}
                  helperText={t("send.emailsHelp")}
                  required
                  rows={3}
                  onChange={(e) => {
                    setEmailsRaw(e.target.value)
                  }}
                />
                {(valid.length > 0 || invalid.length > 0) && (
                  <p className="text-xs">
                    {valid.length > 0 && (
                      <span className="text-muted-foreground">
                        {t("send.validCount", { count: valid.length })}
                      </span>
                    )}
                    {valid.length > 0 && invalid.length > 0 && " · "}
                    {invalid.length > 0 && (
                      <span className="text-destructive">
                        {t("send.invalidList", { list: invalid.join(", ") })}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-start gap-4">
                <FormSelect
                  name="locale"
                  label={t("send.locale")}
                  options={localeOptions}
                  onChange={(value) => {
                    setChosenLocale(value)
                  }}
                />
                <FormControl name="briefId">
                  {({ field }) => (
                    <BriefCombobox
                      label={t("send.brief")}
                      value={field.value as Uuid | null}
                      onChange={(value) => {
                        field.onChange(value)
                        setBriefId(value)
                      }}
                    />
                  )}
                </FormControl>
              </div>
              <FormTextarea
                name="personalMessage"
                label={t("send.personalMessage")}
                rows={2}
                onChange={(e) => {
                  setPersonalMessage(e.target.value)
                }}
              />
              {valid.length > 0 && !!balance && !!unitPrice && !!totalPrice && (
                <p className="text-sm text-muted-foreground">
                  {t("money:send.costSummary", {
                    count: valid.length,
                    unit: unitPrice.amount,
                    total: totalPrice.withTax,
                    balance: balance.available,
                  })}
                </p>
              )}
              {/* Terms §9.3: show the withdrawal-right waiver at the commit
                  point, but not when there is no commit button (the
                  ask-an-owner case) — issue #90 */}
              {valid.length > 0 && (shortfall <= 0 || isOwner) && (
                <p className="text-xs text-muted-foreground">
                  {t("consent.send")}
                </p>
              )}
              <div>
                {shortfall > 0 ? (
                  isOwner ? (
                    <Button
                      variant="primary"
                      type="button"
                      disabled={valid.length === 0}
                      onClick={() => {
                        setTopUpOpen(true)
                      }}
                    >
                      {t("money:send.topUpAndSend", { count: valid.length })}
                    </Button>
                  ) : (
                    <p className="text-sm text-destructive">
                      {t("money:self.askOwner")}
                    </p>
                  )
                ) : (
                  <FormSubmitButton disabled={valid.length === 0}>
                    {valid.length > 0
                      ? t("send.submit", { count: valid.length })
                      : t("send.submitEmpty")}
                  </FormSubmitButton>
                )}
              </div>
            </div>
          </ApiFormWrapper>
          {isOwner && (
            <TopUp
              action={sendAssignmentsAction(
                valid,
                locale,
                personalMessage.trim() || null,
                briefId,
              )}
              shortfall={shortfall}
              open={topUpOpen}
              onOpenChange={setTopUpOpen}
              onComplete={resetForm}
            />
          )}
        </CardContent>
      )}
    </Card>
  )
}
